/**
 * Textos de `screening` en español — la fuente. `en/screening.ts` tiene que tener
 * exactamente las mismas claves (lo exige el tipo), así que una clave nueva
 * acá rompe el typecheck hasta que alguien la traduce.
 *
 * Interpolación con funciones, no con plantillas "{n}": el compilador revisa
 * los argumentos y cada idioma arma su propia gramática (plurales, orden).
 *
 * OJO con lo que NO está acá: los valores que VIAJAN al servidor (el string de
 * "desde cuándo puedes empezar", las frases de las píldoras físicas, la pregunta
 * que se le manda al coach) se quedan en `lib/screening.ts` y en el componente,
 * en español, porque son parte del contrato. Acá sólo vive lo que se ve.
 */
const screening = {
  /** Título y bajada de cada paso, por `id` de `SCREENING_STEPS`. */
  steps: {
    basics: {
      title: 'Empecemos por ti',
      lede: 'Así es como te van a llamar los empleadores.',
    },
    target: {
      title: 'Qué trabajo buscas',
      lede: 'Con esto buscamos las ofertas que te sirven.',
    },
    experience: {
      title: 'Dónde has trabajado',
      lede: 'Cuéntalo hablando: sale mejor que escrito, y así queda tu hoja de vida.',
    },
    skills: {
      title: 'Qué sabes hacer',
      lede: 'Las máquinas que manejas y los idiomas que hablas suman mucho.',
    },
    availability: {
      title: 'Cuándo puedes empezar',
      lede: 'Casi todas estas ofertas son por temporada y arrancan en fecha fija.',
    },
  },

  /** Las etiquetas de los vocabularios cerrados, por valor del enum. */
  options: {
    visa: {
      h2b: {
        label: 'H-2B — trabajo temporal',
        hint: 'Hotelería, construcción, limpieza, cocina, jardinería.',
      },
      h2a_jo: {
        label: 'H-2A — trabajo agrícola',
        hint: 'Cosecha, empaque, invernaderos, ganadería.',
      },
    },
    workArea: {
      agricultura: 'Agricultura',
      paisajismo: 'Jardinería y paisajismo',
      construccion: 'Construcción',
      hoteleria: 'Hotelería',
      limpieza: 'Limpieza',
      cocina: 'Cocina',
      bodega: 'Bodega',
      otro: 'Otra',
    },
    languageLevel: {
      basic: 'Básico',
      conversational: 'Me defiendo',
      fluent: 'Fluido',
    },
    /** Por `id` de `START_WHEN_OPTIONS`; el `value` que viaja no se traduce. */
    startWhen: {
      now: 'De inmediato',
      month: 'En un mes',
      contract: 'La fecha del contrato',
      unsure: 'No estoy seguro',
    },
    /** Por id de `PHYSICAL_CHIP_IDS`; la frase que viaja no se traduce. */
    physicalChips: {
      lift: 'Puedo levantar 50 libras o más',
      standing: 'Aguanto turnos largos de pie',
      heat: 'Trabajo bien en clima caliente',
      early: 'Puedo madrugar',
    },
  },

  /** Los errores de `validateStep()`. */
  errors: {
    fullName: 'Escribe tu nombre completo.',
    cityCountry: 'Dinos en qué ciudad y país vives.',
    phoneIncomplete: 'Revisa el número: elige tu país y escríbelo sin el indicativo.',
    maxChars: (n: number) => `Máximo ${n} caracteres.`,
    maxItems: (n: number) => `Máximo ${n}.`,
    pickVisa: 'Elige al menos una.',
    pickArea: 'Elige al menos un área.',
    yearsNumber: 'Escribe el número de años. Si es tu primera vez, escribe 0.',
    yearsRange: 'Escribe un número entre 0 y 60.',
    company: 'Falta el nombre de la empresa o la finca.',
    role: 'Falta el cargo que tenías.',
    duties: 'Cuéntanos qué hacías ahí: habla o escribe.',
    startYear: (min: number, max: number) =>
      `Escribe el año en que entraste, entre ${min} y ${max}.`,
    endYear: 'Escribe el año en que saliste, o marca que sigues ahí.',
    endBeforeStart: 'El año de salida no puede ser anterior al de entrada.',
    education: 'Cuéntanos hasta dónde estudiaste.',
    skillLength: 'Cada una en pocas palabras, máximo 80 caracteres.',
    language: 'Escribe el idioma o quita la fila.',
    relocate: 'Elige una de las dos.',
    startWhen: 'Dinos desde cuándo puedes empezar.',
  },

  /** Los `issues` del 400, por campo raíz, cuando no calzan con `errors`. */
  issues: {
    lastJobs: 'Revisa los datos de este empleo.',
    skills: 'Revisa esta lista.',
    languages: 'Revisa este idioma.',
    availability: 'Revisa tu disponibilidad.',
    fallback: 'Revisa este dato.',
  },

  wizard: {
    stepOf: (n: number, total: number) => `Paso ${n} de ${total}`,
    progressLabel: 'Avance del cuestionario',
    cancel: 'Cancelar',
    back: 'Atrás',
    next: 'Siguiente',
    create: 'Crear mi hoja de vida',
    createNote:
      'La escribimos en inglés: es el idioma en que la leen los empleadores en Estados Unidos. Vas a poder revisarla antes de usarla.',
    optional: ' (opcional)',
  },

  basics: {
    fullName: 'Nombre completo',
    fullNamePlaceholder: 'Como aparece en tu pasaporte',
    cityCountry: 'Ciudad y país donde vives',
    cityCountryHint: 'Ejemplo: Cali, Colombia',
    phone: 'Tu WhatsApp',
    phoneHint: 'Te escribimos por WhatsApp sobre tus postulaciones y si un empleador quiere evaluar tu inglés.',
  },

  target: {
    visa: '¿A qué visa quieres aplicar?',
    visaHint: 'Puedes marcar las dos.',
    areas: '¿En qué has trabajado o quieres trabajar?',
    areasHint: 'Marca todas las que apliquen.',
    years: '¿Cuántos años llevas trabajando?',
    yearsHint: 'En total, contando todos tus trabajos. Si es tu primera vez, escribe 0.',
  },

  experience: {
    intro:
      'Cuenta hasta tres empleos, empezando por el más reciente. Cuentan también el trabajo en finca, por días o por temporada.',
    job: (n: number) => `Empleo ${n}`,
    remove: 'Quitar',
    company: 'Empresa o finca',
    role: '¿Qué cargo tenías?',
    rolePlaceholder: 'Ejemplo: Operario de cosecha',
    startYear: 'Año en que entraste',
    endYear: 'Año en que saliste',
    current: 'Todavía trabajo ahí',
    duties: '¿Qué hacías en un día normal?',
    dutiesInvitation:
      'Dale al micrófono y cuéntalo como se lo contarías a un amigo: qué hacías desde que llegabas, qué máquinas manejabas, de qué tamaño era el lugar.',
    dutiesPlaceholder: 'Llegaba a las 5, recogía café en la parte alta de la finca…',
    addFirst: 'Agregar un empleo',
    addAnother: 'Agregar otro empleo',
    noJobs:
      'Si nunca has tenido un empleo formal, puedes seguir sin llenar nada. Lo que sabes hacer se cuenta en el paso siguiente.',
  },

  skills: {
    skills: 'Máquinas, herramientas y habilidades',
    skillsInvitation:
      'Dilas de corrido al micrófono —guadaña, montacargas, soldadura— y las separamos nosotros.',
    skillsPlaceholder: 'Guadaña, montacargas, un poco de soldadura',
    addToList: 'Agregar a la lista',
    removeSkill: (skill: string) => `Quitar ${skill}`,
    languages: 'Idiomas',
    languagePlaceholder: 'Inglés',
    levelOf: (language: string) => `Nivel de ${language || 'idioma'}`,
    removeLanguage: (n: number) => `Quitar idioma ${n}`,
    addLanguage: 'Agregar idioma',
    education: '¿Hasta dónde estudiaste?',
    educationInvitation:
      'Cuéntalo hablando: hasta qué grado llegaste, si hiciste algún curso o técnico, dónde y de qué.',
    educationPlaceholder: 'Terminé bachillerato y un curso de refrigeración en el SENA',
  },

  availability: {
    relocate: '¿Puedes mudarte a Estados Unidos por la temporada?',
    relocateYes: 'Sí, puedo mudarme',
    relocateNo: 'Por ahora no',
    startWhen: '¿Desde cuándo puedes empezar?',
    otherDate: 'Otra fecha',
    otherDateLabel: 'Otra fecha para empezar',
    otherDatePlaceholder: 'Ejemplo: después de la cosecha de abril',
    physical: 'Capacidad física',
    physicalIntro:
      'Estos trabajos son de esfuerzo y el empleador lo pregunta siempre. Marca lo que aplique y cuéntanos el resto.',
    physicalInvitation:
      '¿Algo más que deberían saber? Cuéntalo al micrófono: cuánto peso cargabas, cuántas horas seguidas, con qué clima.',
    physicalPlaceholder: 'Cargaba bultos de 50 kilos toda la mañana, aguanto sol fuerte',
  },

  /** El campo hablado (`VoiceAnswer`). */
  voice: {
    transcribing: 'Pasando tu voz a texto',
    stop: 'Detener la grabación',
    record: 'Grabar mi respuesta',
    addMore: 'Toca para agregar algo más',
    tapAndTalk: 'Toca y habla',
    secondsLeft: (n: number) =>
      `Te quedan ${n} segundos — cierra la idea y sigues en otra grabación.`,
    timeUp: 'Guardamos lo grabado. Puedes seguir en otra grabación.',
    preferWriting: 'Prefiero escribir',
    thinking: 'Escuchando lo que contaste…',
    coachHintMic: 'Contesta con el micrófono y se suma a lo que ya contaste.',
    coachHintText: 'Escríbelo arriba y se suma a lo que ya contaste.',
    continueAsIs: 'Continuar así',
    charsLeft: (n: number) =>
      n > 0
        ? `Te quedan ${n} caracteres en esta respuesta.`
        : 'Llegaste al máximo de esta respuesta. Lo que falte lo puedes contar en la siguiente.',
    recordingFailed: 'No pudimos usar tu grabación.',
    problem: {
      denied:
        'No nos diste permiso del micrófono, y así queda. Escríbelo aquí: mientras más cuentes de lo que hacías, mejor te queda la hoja de vida.',
      unsupported: 'Este navegador no sabe grabar. Escríbelo aquí y seguimos igual.',
      failed: 'No pudimos abrir el micrófono. Prueba otra vez, o escríbelo aquí.',
      empty:
        'No alcanzamos a oírte. Acerca el teléfono y mantén el botón hasta terminar, o escríbelo aquí.',
    },
  },

  /** Los códigos de `POST /screening/transcribe`, en `voiceClient`. */
  transcribeErrors: {
    audio_too_large: 'El audio salió muy largo. Grábalo en pedazos más cortos, o escríbelo aquí.',
    empty_audio: 'No alcanzamos a oírte. Acerca el teléfono y prueba otra vez, o escríbelo aquí.',
    unsupported_audio_type: 'Tu teléfono grabó en un formato que no leemos. Escríbelo aquí y seguimos.',
    invalid_audio: 'Algo pasó con la grabación. Prueba otra vez, o escríbelo aquí.',
    rate_limited: 'Grabaste muchas veces seguidas. Escríbelo aquí y seguimos.',
    transcription_failed: 'No pudimos pasar tu voz a texto ahora. Escríbelo aquí y seguimos.',
    unauthorized: 'Tu sesión se venció. Vuelve a entrar.',
    network: 'Sin conexión. Revisa tu internet, o escríbelo aquí.',
    unknown: 'No pudimos usar tu grabación. Escríbelo aquí y seguimos.',
  },

  /** Los códigos de `POST /screening` y `/regenerate`, en `screeningClient`. */
  apiErrors: {
    no_membership: 'Estamos terminando de abrir tu cuenta. Intenta de nuevo.',
    invalid_screening: 'Revisa los datos marcados y vuelve a intentar.',
    screening_failed: 'No pudimos armar tu hoja de vida. Intenta de nuevo en un momento.',
    invalid_request: 'No pudimos enviar tu ajuste. Intenta de nuevo.',
    cv_not_found: 'No encontramos esa hoja de vida. Vuelve a llenar el cuestionario.',
    not_a_screening_cv: 'Esa hoja de vida se subió como archivo, no la podemos reescribir.',
    missing_screening_answers: 'Nos faltan tus respuestas. Vuelve a llenar el cuestionario.',
    regeneration_limit: 'Ya la ajustamos tres veces. Para cambiar datos, vuelve al cuestionario.',
    set_primary_failed: 'No pudimos cambiar tu hoja de vida. Intenta de nuevo.',
    unauthorized: 'Tu sesión se venció. Vuelve a entrar.',
    network: 'Sin conexión. Revisa tu internet e intenta de nuevo.',
    unknown: 'Algo falló de nuestro lado. Intenta de nuevo.',
  },

  /** Los errores que pinta la pantalla (`JobApplyScreeningPage`). */
  page: {
    checkMarked: 'Revisa los datos marcados y vuelve a intentar.',
    submitFailed: 'Algo falló. Intenta de nuevo.',
    regenerateFailed: 'No pudimos rehacerla.',
    prefilling: 'Trayendo lo que ya contestaste…',
  },

  review: {
    eyebrow: 'Tu hoja de vida',
    title: 'Así quedó',
    lede: 'Está en inglés porque así la leen los empleadores en Estados Unidos. No es un error: contestaste en español y nosotros la tradujimos.',
    summary: 'Resumen',
    skills: 'Lo que buscamos con esto',
    notPrimary:
      'Guardamos esta hoja de vida, pero tus postulaciones siguen saliendo con la que ya tenías. ¿Con cuál quieres postular?',
    useThis: 'Usar esta hoja de vida',
    keepCurrent: 'Seguir con la que ya tenía',
    confirm: 'Se ve bien, continuar',
    improve: 'Mejorarla',
    adjustmentsLeft: (n: number) => `Puedes pedir ${n} ${n === 1 ? 'ajuste' : 'ajustes'} más.`,
    limitBefore: 'Ya la ajustamos tres veces.',
    limitLink: 'Cambia tus respuestas',
    limitAfter: 'si falta algo.',
    feedbackLabel: '¿Qué quieres que suene distinto?',
    feedbackPlaceholder: 'Ejemplo: que el resumen hable más de mi experiencia en cosecha.',
    rewriteBefore: 'Reescribimos el texto con lo que ya contaste. Para agregar datos nuevos,',
    rewriteLink: 'vuelve al cuestionario',
    rewriteAfter: '.',
    cancel: 'Cancelar',
    redo: 'Rehacerla',
    backToAnswers: 'Volver a mis respuestas',
    scoreScope: 'Qué tan fuerte quedó',
    addMissing: 'Agregar estos datos',
    addMissingHint: 'Vuelves al cuestionario con todo lo que ya contestaste.',
  },
  /** Las preguntas que viajan al coach con cada respuesta hablada (no se ven). */
  coachQuestions: {
    duties: '¿Qué hacías en un día normal de trabajo?',
    skills: '¿Qué máquinas y herramientas sabes manejar?',
    education: '¿Hasta dónde estudiaste?',
    physical: '¿Qué esfuerzo físico aguantas en un día de trabajo?',
  },
};

export default screening;
